import {
    BymoPageProps,
    bymoPagePropsGetInnerPageBySlug,
    DoppeViewerPageInfo,
    doppeViewerPageInfoCreateBlogPostsList,
    doppeViewerPageInfoCreateContentPage,
    doppeViewerPageInfoCreateEventsList,
    doppeViewerPageInfoCreateHome,
    doppeViewerPageInfoCreateProductItem,
    doppeViewerPageInfoCreateProductsList,
    doppeViewerPageInfoCreateServicesList,
    DoppeViewerPageType
} from '../../../client-server-common';
import {bymoPageFindPageById} from '../../../client-server-common/bymo-page-utils/bymo-page-utils';
import {DoppeDtoInnerPageType} from '../../../client-server-common/types/doppe-dto-inner-page';

/**
 * Convert a page ID to DoppeViewerPageInfo
 * Requires bymoPageProps to resolve page details (type, slug, etc.)
 * 
 * @param pageId - The page ID to convert (null for home page)
 * @param bymoPageProps - Page props to look up page details
 * @returns Full DoppeViewerPageInfo object
 */
export function bymoEditorCurrentPageScopeResolvePageInfo(
    pageId: string | null,
    bymoPageProps: BymoPageProps
): DoppeViewerPageInfo {
    // Home page (null page ID)
    if (pageId === null) {
        return doppeViewerPageInfoCreateHome();
    }
    
    // Find the page in bymoPageProps (type assertion: these helpers are only used with inner pages)
    const page = bymoPageFindPageById(pageId, bymoPageProps);
    if (!page) {
        // Page not found, default to home
        return doppeViewerPageInfoCreateHome();
    }
    
    // Create pageInfo based on page type
    switch (page.type) {
        case DoppeDtoInnerPageType.Products:
            return doppeViewerPageInfoCreateProductsList();
            
        case DoppeDtoInnerPageType.BlogPosts:
            return doppeViewerPageInfoCreateBlogPostsList();
            
        case DoppeDtoInnerPageType.Events:
            return doppeViewerPageInfoCreateEventsList();
            
        case DoppeDtoInnerPageType.Bookings:
            return doppeViewerPageInfoCreateServicesList();
            
        case DoppeDtoInnerPageType.Content:
            return doppeViewerPageInfoCreateContentPage(page.slug);
            
        case DoppeDtoInnerPageType.Product:
            return doppeViewerPageInfoCreateProductItem(page.slug);
            
        // Note: BlogPost, Event, and BookingService item pages are not yet defined in DoppeDtoInnerPage union
        // Add cases when those page types are implemented:
        // case DoppeDtoInnerPageType.BlogPost:
        //     return doppeViewerPageInfoCreateBlogPostItem(page.slug);
        // case DoppeDtoInnerPageType.Event:
        //     return doppeViewerPageInfoCreateEventItem(page.slug);
        // case DoppeDtoInnerPageType.BookingService:
        //     return doppeViewerPageInfoCreateServiceItem(page.slug);
        
        default:
            // Unknown page type, default to home
            return doppeViewerPageInfoCreateHome();
    }
}

/**
 * Extract page ID from DoppeViewerPageInfo
 * For backward compatibility with code expecting page IDs
 * 
 * For vertical pages, the pageId IS the DoppeDtoInnerPageType enum value.
 * For example:
 * - ProductsList → DoppeDtoInnerPageType.Products
 * - ProductItem → DoppeDtoInnerPageType.Product (only Product has a singular version)
 * - BlogPostItem → DoppeDtoInnerPageType.BlogPosts (uses plural for list page)
 * 
 * @param pageInfo - The page info object
 * @param bymoPageProps - Not used, kept for backward compatibility
 * @returns Page ID or null for home page
 */
export function bymoEditorCurrentPageScopeGetPageId(
    pageInfo: DoppeViewerPageInfo,
    bymoPageProps?: BymoPageProps
): string | null {
    // Home page
    if (pageInfo.pageType === DoppeViewerPageType.Home) {
        return null;
    }
    
    // Content pages: calculate pageId from slug using bymoPageProps
    if (pageInfo.pageType === DoppeViewerPageType.ContentPage) {
        if (!bymoPageProps) {
            return null;
        }
        const innerPage = bymoPagePropsGetInnerPageBySlug(bymoPageProps, pageInfo.slug);
        return innerPage ? innerPage.id : null;
    }
    
    // Vertical list and item pages - return the DoppeDtoInnerPageType directly
    // The page id for these pages IS the DoppeDtoInnerPageType enum value
    switch (pageInfo.pageType) {
        // List pages - map to DoppeDtoInnerPageType
        case DoppeViewerPageType.ProductsList:
            return DoppeDtoInnerPageType.Products;
        
        case DoppeViewerPageType.BlogPostsList:
            return DoppeDtoInnerPageType.BlogPosts;
            
        case DoppeViewerPageType.EventsList:
            return DoppeDtoInnerPageType.Events;
            
        case DoppeViewerPageType.ServicesList:
            return DoppeDtoInnerPageType.Bookings;
        
        // Item pages - return the corresponding DoppeDtoInnerPageType
        // Note: Only Product has a singular version, others use the list page type
        case DoppeViewerPageType.ProductItem:
            return DoppeDtoInnerPageType.Product;
        
        case DoppeViewerPageType.BlogPostItem:
            return DoppeDtoInnerPageType.BlogPost;
            
        case DoppeViewerPageType.EventItem:
            return DoppeDtoInnerPageType.Event;
            
        case DoppeViewerPageType.ServiceItem:
            return DoppeDtoInnerPageType.BookingService;
            
        default:
            return null;
    }
}

